import React, { useEffect, useMemo, useState } from 'react';
import { getJoinEligibility, getSeatsRemaining } from '../utils/tripSchedule';
import {
  getBrowseLegOptions,
  getDefaultLegValue,
  getTripLegEstimates,
} from '../utils/tripPricingDisplay';
import TripScheduleDetailHeader from './trip/TripScheduleDetailHeader';
import TripPriceSeatsBlock from './trip/TripPriceSeatsBlock';
import TripSeatFigures from './trip/TripSeatFigures';

const TripScheduleDetailPanel = ({
  trip,
  isJoined = false,
  isCreator = false,
  isAuthenticated = true,
  userId = null,
  onJoin,
  onClose,
  className = '',
}) => {
  const legOptions = useMemo(() => (trip ? getBrowseLegOptions(trip) : []), [trip]);

  const legEstimates = useMemo(
    () => (trip && legOptions.length ? getTripLegEstimates(trip, legOptions) : []),
    [trip, legOptions]
  );

  const defaultLegValue = useMemo(
    () => (trip ? getDefaultLegValue(trip, legOptions) : ''),
    [trip, legOptions]
  );

  const [selectedLegValue, setSelectedLegValue] = useState(defaultLegValue);

  useEffect(() => {
    setSelectedLegValue(defaultLegValue);
  }, [trip?.id, defaultLegValue]);

  const viewerSegment = useMemo(() => {
    if (!trip || !userId || !isJoined) return null;
    const participant = trip.participants?.find((p) => p.user_id === userId);
    return participant?.segment ?? null;
  }, [trip, userId, isJoined]);

  if (!trip) return null;
  const seatsRemaining = getSeatsRemaining(trip);
  const eligibility = getJoinEligibility(trip, { isJoined, isCreator });

  const statusBadgeType = trip.status === 'confirmed' ? 'confirmed' : 'warning';
  const statusLabel = trip.status === 'confirmed' ? 'Confirmed Trip' : 'Awaiting Riders';

  let joinLabel = 'Join trip';
  let joinDisabled = false;

  if (!isAuthenticated) {
    joinLabel = 'Sign in to join';
    joinDisabled = true;
  } else if (!eligibility.joinable) {
    joinLabel = eligibility.deadlineLabel || 'Join unavailable';
    joinDisabled = true;
  }

  const isFull = seatsRemaining <= 0;

  return (
    <div
      id="trip-schedule-detail"
      className={`bg-white rounded-2xl border border-[#E8DED1] shadow-md overflow-hidden flex flex-col ${className}`}
    >
      <TripScheduleDetailHeader
        trip={trip}
        statusBadgeType={statusBadgeType}
        statusLabel={statusLabel}
        joinDeadlineLabel={eligibility.deadlineLabel}
        onClose={onClose}
      />

      <div className="p-5 lg:grid lg:grid-cols-2 gap-6 lg:gap-8 flex-1">
        <div className="space-y-4">
          <TripSeatFigures trip={trip} />

          <div className="flex flex-wrap items-center gap-2">
            {isJoined && (
              <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full uppercase">
                Joined
              </span>
            )}
            {isCreator && (
              <span className="text-[10px] font-bold text-[#A44432] bg-[#A44432]/10 px-2.5 py-1 rounded-full uppercase">
                Created
              </span>
            )}
            {isFull && !isJoined && !isCreator && (
              <span className="text-[10px] font-bold text-stone-500 bg-stone-200 px-2.5 py-1 rounded-full uppercase">
                Trip Full
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:border-l lg:border-[#E8DED1]/80 lg:pl-6">
          <TripPriceSeatsBlock
            trip={trip}
            legOptions={legOptions}
            legEstimates={legEstimates}
            selectedLegValue={selectedLegValue}
            onLegChange={setSelectedLegValue}
            isJoined={isJoined}
            isCreator={isCreator}
            viewerSegment={viewerSegment}
          />
          <button
            type="button"
            disabled={joinDisabled}
            onClick={() => !joinDisabled && onJoin?.(trip, selectedLegValue)}
            className="btn-primary px-6 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full"
          >
            {joinLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripScheduleDetailPanel;
